// src/pages/AdminAssignSetter.jsx
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import API_URL from '../config/api';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import '../styles/AdminAssignSetter.css';

function AdminAssignSetter() {
  const { appointmentId } = useParams();
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState(null);
  const [availableSetters, setAvailableSetters] = useState([]);
  const [selectedSetter, setSelectedSetter] = useState(null);
  const [assigning, setAssigning] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadAssignmentData();
  }, [appointmentId]);

  const loadAssignmentData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/availability/assign-setter/${appointmentId}`);
      setAppointment(response.data.appointment);
      setAvailableSetters(response.data.availableSetters);
    } catch (error) {
      console.error('Error loading assignment data:', error);
      alert('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSetter = async () => {
    if (!selectedSetter) {
      alert('Please select a setter');
      return;
    }

    setAssigning(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/availability/assign-setter/${appointmentId}`,
        { setterId: selectedSetter.id }
      );

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (error) {
      console.error('Assignment error:', error);
      const errorMessage = error.response?.data?.error || 'Failed to assign setter';
      alert(errorMessage);
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading appointment details...</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="success-container">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="success-message"
        >
          <div className="success-icon">✓</div>
          <h2>Setter Assigned Successfully!</h2>
          <p>The appointment has been assigned to {selectedSetter.name}</p>
        </motion.div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div className="error-container">
        <h2>Appointment Not Found</h2>
        <p>The appointment you're looking for doesn't exist.</p>
      </div>
    );
  }

  const formattedTime = appointment.startAt 
    ? formatInTimeZone(
        new Date(appointment.startAt),
        'America/Denver',
        'EEEE, MMMM d, yyyy \'at\' h:mm a zzz'
      )
    : 'Time not available';

  return (
    <div className="admin-assign-page">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="assign-card"
        >
          <h1>Assign Setter to Appointment</h1>

          {/* Appointment Details */}
          <div className="appointment-details">
            <h2>Appointment Details</h2>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="label">Customer:</span>
                <span className="value">{appointment.contact?.fullName}</span>
              </div>
              <div className="detail-item">
                <span className="label">Email:</span>
                <span className="value">{appointment.contact?.email}</span>
              </div>
              <div className="detail-item">
                <span className="label">Phone:</span>
                <span className="value">{appointment.contact?.phoneNumber}</span>
              </div>
              <div className="detail-item">
                <span className="label">Time:</span>
                <span className="value">{formattedTime}</span>
              </div>
              <div className="detail-item full-width">
                <span className="label">Location:</span>
                <span className="value">{appointment.appointmentLocation}</span>
              </div>
              <div className="detail-item">
                <span className="label">Calendar:</span>
                <span className="value">{appointment.calendar}</span>
              </div>
            </div>
          </div>

          {/* Setter Selection */}
          <div className="setter-selection">
            <h2>Select a Setter</h2>
            {availableSetters.length === 0 ? (
              <p className="no-setters">No setters available for this time slot</p>
            ) : (
              <div className="setters-grid">
                {availableSetters.map((setter) => (
                  <motion.div
                    key={setter.id}
                    className={`setter-card ${selectedSetter?.id === setter.id ? 'selected' : ''}`}
                    onClick={() => setSelectedSetter(setter)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {setter.image && (
                      <img src={setter.image} alt={setter.name} className="setter-image" />
                    )}
                    <div className="setter-info">
                      <h3>{setter.name}</h3>
                      <p className="setter-title">{setter.jobTitle}</p>
                      {setter.team && <p className="setter-team">Team: {setter.team}</p>}
                    </div>
                    {selectedSetter?.id === setter.id && (
                      <div className="selected-indicator">✓</div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Assign Button */}
          {availableSetters.length > 0 && (
            <motion.button
              className="assign-button"
              onClick={handleAssignSetter}
              disabled={!selectedSetter || assigning}
              whileHover={{ scale: selectedSetter && !assigning ? 1.02 : 1 }}
              whileTap={{ scale: selectedSetter && !assigning ? 0.98 : 1 }}
            >
              {assigning ? 'Assigning...' : 'Assign Setter'}
            </motion.button>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default AdminAssignSetter;